import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Plus, Loader2, Pencil, Copy, Star, Trash2, Eye, MousePointerClick,
  Download, Upload, Settings2, GripVertical, FolderCog, Layers,
} from "lucide-react";

const TYPES = [
  { v: "external", label: "Link externo" },
  { v: "internal", label: "Página interna do EDUCARTE" },
  { v: "quiz", label: "Quiz EDUCARTE" },
  { v: "file", label: "Arquivo para download" },
  { v: "link_file", label: "Link + arquivo" },
];

const emptyForm = {
  title: "", description: "", class_slug: "", subject: "Outros", activity_type: "external",
  external_url: "", internal_url: "", file_url: "", file_name: "", file_type: "", file_size: 0,
  thumbnail_url: "", activity_date: "", status: "active", visibility: "public", featured: false,
  publish_at: "", expire_at: "",
};

export default function GerenciarAtividades() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterClass, setFilterClass] = useState("all");
  const [sort, setSort] = useState("recent");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [imgUploading, setImgUploading] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [copyTarget, setCopyTarget] = useState(null);
  const [copySlugs, setCopySlugs] = useState([]);
  const [classDialog, setClassDialog] = useState(false);
  const [dragId, setDragId] = useState(null);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [c, s, a] = await Promise.all([
        api.get("/atividades/classes"),
        api.get("/atividades/subjects"),
        api.get("/atividades/manage/activities"),
      ]);
      setClasses(c.data);
      setSubjects(s.data);
      setActivities(a.data);
    } catch {
      toast.error("Erro ao carregar atividades.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const classNameOf = (slug) => classes.find((c) => c.slug === slug)?.name || slug;

  const visible = useMemo(() => {
    let list = filterClass === "all" ? activities : activities.filter((a) => a.class_slug === filterClass);
    const arr = [...list];
    if (sort === "recent") arr.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    else if (sort === "old") arr.sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
    else if (sort === "alpha") arr.sort((a, b) => a.title.localeCompare(b.title));
    else if (sort === "subject") arr.sort((a, b) => (a.subject || "").localeCompare(b.subject || ""));
    else if (sort === "custom") arr.sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
    return arr;
  }, [activities, filterClass, sort]);

  const canDrag = sort === "custom" && filterClass !== "all";

  const onDrop = async (targetId) => {
    if (!dragId || dragId === targetId) return setDragId(null);
    const list = [...visible];
    const from = list.findIndex((a) => a.id === dragId);
    const to = list.findIndex((a) => a.id === targetId);
    setDragId(null);
    if (from < 0 || to < 0) return;
    const [moved] = list.splice(from, 1);
    list.splice(to, 0, moved);
    const orderedIds = list.map((a) => a.id);
    setActivities((prev) =>
      prev.map((a) =>
        a.class_slug === filterClass ? { ...a, display_order: orderedIds.indexOf(a.id) } : a
      )
    );
    try {
      await api.put("/atividades/manage/activities/reorder", { class_slug: filterClass, ordered_ids: orderedIds });
    } catch {
      toast.error("Erro ao reordenar.");
      loadAll();
    }
  };

  const openNew = () => {
    setEditingId(null);
    setForm({ ...emptyForm, class_slug: classes[0]?.slug || "" });
    setDialogOpen(true);
  };

  const openEdit = (a) => {
    setEditingId(a.id);
    setForm({ ...emptyForm, ...a });
    setDialogOpen(true);
  };

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/atividades/manage/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, ...res.data }));
      toast.success("Arquivo enviado.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha no upload.");
    } finally {
      setUploading(false);
    }
  };

  const handleImage = async (file) => {
    if (!file) return;
    setImgUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/atividades/manage/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, thumbnail_url: res.data.thumbnail_url }));
      toast.success("Imagem enviada.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha no envio da imagem.");
    } finally {
      setImgUploading(false);
    }
  };

  const save = async () => {
    if (!form.title.trim() || !form.class_slug) return toast.error("Informe título e turma.");
    setSaving(true);
    try {
      if (editingId) await api.put(`/atividades/manage/activities/${editingId}`, form);
      else await api.post("/atividades/manage/activities", form);
      toast.success(editingId ? "Atividade atualizada." : "Atividade criada.");
      setDialogOpen(false);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao salvar.");
    } finally {
      setSaving(false);
    }
  };

  const act = async (fn, okMsg) => {
    try { await fn(); if (okMsg) toast.success(okMsg); loadAll(); }
    catch (e) { toast.error(e.response?.data?.detail || "Ação não permitida."); }
  };

  const doCopy = async () => {
    if (!copyTarget || copySlugs.length === 0) return;
    await act(() => api.post(`/atividades/manage/activities/${copyTarget.id}/copy`, { class_slugs: copySlugs }), "Copiada para outras turmas.");
    setCopyTarget(null); setCopySlugs([]);
  };

  return (
    <DashboardLayout>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-black text-brand-ink">Atividades por Turma</h1>
          <p className="text-brand-muted text-sm mt-1">Disponibilize links, jogos, quizzes e arquivos para os alunos.</p>
        </div>
        <div className="flex gap-2">
          {isAdmin && (
            <Button variant="outline" onClick={() => setClassDialog(true)} className="rounded-full" data-testid="manage-classes-btn">
              <FolderCog className="w-4 h-4 mr-2" /> Gerenciar turmas
            </Button>
          )}
          <Button onClick={openNew} className="rounded-full bg-brand-blue hover:bg-blue-700" data-testid="new-activity-btn">
            <Plus className="w-4 h-4 mr-2" /> Nova atividade
          </Button>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-3">
        <Select value={filterClass} onValueChange={setFilterClass}>
          <SelectTrigger className="w-48 rounded-full" data-testid="filter-class"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas as turmas</SelectItem>
            {classes.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger className="w-44 rounded-full" data-testid="sort-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="recent">Mais recentes</SelectItem>
            <SelectItem value="old">Mais antigas</SelectItem>
            <SelectItem value="alpha">Ordem alfabética</SelectItem>
            <SelectItem value="subject">Disciplina</SelectItem>
            <SelectItem value="custom">Ordem personalizada</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {sort === "custom" && filterClass === "all" && (
        <p className="mt-3 text-xs text-amber-600" data-testid="drag-hint">Selecione uma turma específica para arrastar e reordenar as atividades.</p>
      )}
      {canDrag && (
        <p className="mt-3 text-xs text-brand-muted" data-testid="drag-hint-active">Arraste os cartões pelo ícone à esquerda para reordenar.</p>
      )}

      {loading ? (
        <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-brand-blue" /></div>
      ) : visible.length === 0 ? (
        <div className="text-center py-24 text-brand-muted"><Layers className="w-10 h-10 mx-auto mb-3 opacity-40" />Nenhuma atividade. Clique em "Nova atividade".</div>
      ) : (
        <div className="mt-6 space-y-3" data-testid="manage-list">
          {visible.map((a) => (
            <div
              key={a.id}
              data-testid={`manage-row-${a.id}`}
              draggable={canDrag && a.can_edit}
              onDragStart={() => canDrag && setDragId(a.id)}
              onDragOver={(e) => canDrag && e.preventDefault()}
              onDrop={() => canDrag && onDrop(a.id)}
              className={`flex items-center gap-4 bg-white rounded-2xl border p-4 transition-all ${a.status !== "active" ? "opacity-60 border-slate-100" : "border-slate-100"} ${dragId === a.id ? "ring-2 ring-brand-blue" : ""} ${canDrag && a.can_edit ? "cursor-move" : ""}`}
            >
              {canDrag && a.can_edit && (
                <GripVertical className="w-5 h-5 text-slate-300 shrink-0" data-testid={`drag-handle-${a.id}`} />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  {a.featured && <Star className="w-4 h-4 text-brand-yellow fill-brand-yellow" />}
                  <span className="font-semibold text-brand-ink truncate">{a.title}</span>
                  <span className="text-xs bg-slate-100 text-brand-muted px-2 py-0.5 rounded-full">{classNameOf(a.class_slug)}</span>
                  <span className="text-xs text-brand-muted">{a.subject}</span>
                  {a.status !== "active" && <span className="text-xs bg-slate-200 text-slate-600 px-2 py-0.5 rounded-full">Inativa</span>}
                  {a.visibility === "restricted" && <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">Restrita</span>}
                </div>
                <div className="mt-1 flex items-center gap-4 text-xs text-brand-muted">
                  <span className="inline-flex items-center gap-1"><Eye className="w-3.5 h-3.5" /> {a.views || 0}</span>
                  <span className="inline-flex items-center gap-1"><MousePointerClick className="w-3.5 h-3.5" /> {a.accesses || 0}</span>
                  <span className="inline-flex items-center gap-1"><Download className="w-3.5 h-3.5" /> {a.downloads || 0}</span>
                </div>
              </div>
              {a.can_edit ? (
                <div className="flex items-center gap-1">
                  <Button size="icon" variant="ghost" title="Editar" onClick={() => openEdit(a)} data-testid={`edit-${a.id}`}><Pencil className="w-4 h-4" /></Button>
                  <Button size="icon" variant="ghost" title="Destaque" onClick={() => act(() => api.put(`/atividades/manage/activities/${a.id}/featured`))} data-testid={`feature-${a.id}`}><Star className={`w-4 h-4 ${a.featured ? "text-brand-yellow fill-brand-yellow" : ""}`} /></Button>
                  <Button size="icon" variant="ghost" title="Duplicar" onClick={() => act(() => api.post(`/atividades/manage/activities/${a.id}/duplicate`), "Duplicada.")} data-testid={`duplicate-${a.id}`}><Copy className="w-4 h-4" /></Button>
                  <Button size="icon" variant="ghost" title="Copiar p/ turma" onClick={() => { setCopyTarget(a); setCopySlugs([]); }} data-testid={`copy-${a.id}`}><Layers className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => act(() => api.put(`/atividades/manage/activities/${a.id}/status`))} data-testid={`status-${a.id}`}>{a.status === "active" ? "Desativar" : "Ativar"}</Button>
                  <Button size="icon" variant="ghost" title="Excluir" onClick={() => setDeleteTarget(a)} data-testid={`delete-${a.id}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                </div>
              ) : (
                <span className="text-xs text-brand-muted px-2">por {a.created_by_name || "outro professor"}</span>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingId ? "Editar atividade" : "Nova atividade"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Turma *</Label>
                <Select value={form.class_slug} onValueChange={(v) => setF("class_slug", v)}>
                  <SelectTrigger className="mt-1" data-testid="form-class"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>{classes.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Disciplina</Label>
                <Select value={form.subject} onValueChange={(v) => setF("subject", v)}>
                  <SelectTrigger className="mt-1" data-testid="form-subject"><SelectValue /></SelectTrigger>
                  <SelectContent>{subjects.map((s) => <SelectItem key={s.name} value={s.name}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Título *</Label>
              <Input value={form.title} onChange={(e) => setF("title", e.target.value)} placeholder="Ex.: Multiplicação – 4º Ano" className="mt-1" data-testid="form-title" />
            </div>
            <div>
              <Label>Descrição</Label>
              <Textarea value={form.description} onChange={(e) => setF("description", e.target.value)} rows={2} className="mt-1" data-testid="form-description" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Tipo</Label>
                <Select value={form.activity_type} onValueChange={(v) => setF("activity_type", v)}>
                  <SelectTrigger className="mt-1" data-testid="form-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Data</Label>
                <Input value={form.activity_date} onChange={(e) => setF("activity_date", e.target.value)} placeholder="Ex.: 28/09/2026" className="mt-1" data-testid="form-date" />
              </div>
            </div>
            {["external", "quiz", "link_file"].includes(form.activity_type) && (
              <div>
                <Label>Link (URL)</Label>
                <Input value={form.external_url} onChange={(e) => setF("external_url", e.target.value)} placeholder="https://..." className="mt-1" data-testid="form-url" />
              </div>
            )}
            {form.activity_type === "internal" && (
              <div>
                <Label>Página interna (ex.: /quiz/ID/visualizar)</Label>
                <Input value={form.internal_url} onChange={(e) => setF("internal_url", e.target.value)} placeholder="/..." className="mt-1" data-testid="form-internal" />
              </div>
            )}
            {["file", "link_file"].includes(form.activity_type) && (
              <div>
                <Label>Arquivo para download</Label>
                <label className="mt-1 flex items-center justify-center gap-2 h-20 border-2 border-dashed border-slate-200 rounded-xl cursor-pointer hover:border-brand-blue transition-colors text-sm text-brand-muted"
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => { e.preventDefault(); handleUpload(e.dataTransfer.files?.[0]); }}
                  data-testid="form-file-drop">
                  {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Upload className="w-5 h-5" /> {form.file_name ? form.file_name : "Arraste ou clique para enviar (PDF, DOCX, XLSX, ZIP...)"}</>}
                  <input type="file" className="hidden" onChange={(e) => handleUpload(e.target.files?.[0])} data-testid="form-file-input" />
                </label>
              </div>
            )}
            <div>
              <Label>Imagem/capa (opcional)</Label>
              <label className="mt-1 flex items-center gap-2 h-11 px-4 border border-slate-200 rounded-xl cursor-pointer hover:border-brand-blue transition-colors text-sm text-brand-muted" data-testid="form-image-label">
                {imgUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {form.thumbnail_url ? "Imagem selecionada — trocar" : "Enviar imagem (JPG/PNG)"}
                <input type="file" accept="image/*" className="hidden" onChange={(e) => handleImage(e.target.files?.[0])} data-testid="form-image-input" />
              </label>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Visibilidade</Label>
                <Select value={form.visibility} onValueChange={(v) => setF("visibility", v)}>
                  <SelectTrigger className="mt-1" data-testid="form-visibility"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="public">Pública (qualquer visitante)</SelectItem>
                    <SelectItem value="restricted">Restrita (somente logados)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end gap-6 pb-1">
                <div className="flex items-center gap-2"><Switch checked={form.status === "active"} onCheckedChange={(v) => setF("status", v ? "active" : "inactive")} data-testid="form-status" /><span className="text-sm">Ativa</span></div>
                <div className="flex items-center gap-2"><Switch checked={form.featured} onCheckedChange={(v) => setF("featured", v)} data-testid="form-featured" /><span className="text-sm">Destaque</span></div>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Publicar em (opcional)</Label>
                <Input type="datetime-local" value={form.publish_at} onChange={(e) => setF("publish_at", e.target.value)} className="mt-1" data-testid="form-publish" />
              </div>
              <div>
                <Label>Remover em (opcional)</Label>
                <Input type="datetime-local" value={form.expire_at} onChange={(e) => setF("expire_at", e.target.value)} className="mt-1" data-testid="form-expire" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving} className="bg-brand-blue hover:bg-blue-700" data-testid="save-activity-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salvar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir atividade</AlertDialogTitle>
            <AlertDialogDescription>Tem certeza de que deseja excluir "{deleteTarget?.title}"? Esta ação não pode ser desfeita.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction className="bg-red-500 hover:bg-red-600" onClick={() => { const t = deleteTarget; setDeleteTarget(null); act(() => api.delete(`/atividades/manage/activities/${t.id}`), "Excluída."); }} data-testid="confirm-delete-btn">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Copy to classes */}
      <Dialog open={!!copyTarget} onOpenChange={(o) => !o && setCopyTarget(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Copiar para outras turmas</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto">
            {classes.filter((c) => c.slug !== copyTarget?.class_slug).map((c) => {
              const sel = copySlugs.includes(c.slug);
              return (
                <button key={c.slug} onClick={() => setCopySlugs((p) => sel ? p.filter((s) => s !== c.slug) : [...p, c.slug])}
                  className={`px-3 py-2 rounded-xl text-sm border text-left ${sel ? "border-brand-blue bg-brand-blue/10 text-brand-blue font-semibold" : "border-slate-200 text-brand-muted"}`}
                  data-testid={`copy-slug-${c.slug}`}>
                  {c.name}
                </button>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCopyTarget(null)}>Cancelar</Button>
            <Button onClick={doCopy} disabled={copySlugs.length === 0} className="bg-brand-blue hover:bg-blue-700" data-testid="confirm-copy-btn">Copiar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {isAdmin && <ClassManager open={classDialog} onClose={() => setClassDialog(false)} onChanged={loadAll} />}
    </DashboardLayout>
  );
}

function ClassManager({ open, onClose, onChanged }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState("");
  const [icon, setIcon] = useState("");
  const [color, setColor] = useState("#2563eb");

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/atividades/manage/classes"); setList(r.data); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (open) load(); }, [open]);

  const create = async () => {
    if (!name.trim()) return;
    try { await api.post("/atividades/manage/classes", { name, icon, color }); setName(""); setIcon(""); load(); onChanged(); toast.success("Turma criada."); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro."); }
  };
  const update = async (c, patch) => {
    try { await api.put(`/atividades/manage/classes/${c.class_id}`, patch); load(); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro."); }
  };
  const del = async (c) => {
    try { await api.delete(`/atividades/manage/classes/${c.class_id}`); load(); onChanged(); toast.success("Turma excluída."); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro."); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Gerenciar turmas</DialogTitle></DialogHeader>
        <div className="flex items-end gap-2">
          <div className="flex-1"><Label className="text-xs">Nome</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex.: Ensino Médio" data-testid="new-class-name" /></div>
          <div className="w-16"><Label className="text-xs">Ícone</Label><Input value={icon} onChange={(e) => setIcon(e.target.value)} placeholder="EM" /></div>
          <div className="w-14"><Label className="text-xs">Cor</Label><input type="color" value={color} onChange={(e) => setColor(e.target.value)} className="w-full h-10 rounded-md border border-slate-200" /></div>
          <Button onClick={create} className="bg-brand-blue hover:bg-blue-700" data-testid="add-class-btn"><Plus className="w-4 h-4" /></Button>
        </div>
        <div className="mt-2 space-y-2">
          {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : list.map((c) => (
            <div key={c.class_id} className="flex items-center gap-3 border border-slate-100 rounded-xl p-2">
              <span className="w-9 h-9 rounded-lg flex items-center justify-center text-white font-bold text-sm" style={{ backgroundColor: c.color }}>{c.icon}</span>
              <span className="flex-1 text-sm font-medium">{c.name}</span>
              <Switch checked={c.active} onCheckedChange={(v) => update(c, { active: v })} />
              <Button size="icon" variant="ghost" onClick={() => del(c)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
